<!-- ? V2 1XP -->
<script lang="ts">
import getQuoteIdFromUrl from '../../xhr/shopCart/getQuoteIdFromUrl';
import CustomEvents from '../../globals/constants/customEvents';
import { computed, ref, watch } from 'vue';
import Button1XP from '../Button1XP/Button1XP.vue';
import type { WindowWika } from '../../types/window';
import { useStoreConfiguration } from '../../store/configuration/configuration';
import { useStoreTranslations } from '../../store/translations/translations';
import { post } from '../../xhr/post';
import withBaseUrl from '../../xhr/withBaseUrl';
import Notifications from '../Notifications/Notifications.vue';

const _window = window as unknown as WindowWika;
type ResponseError = {
  success: boolean;
  error: string;
};
</script>

<script lang="ts" setup>
const storeConfiguration = useStoreConfiguration();
const storeTranslations = useStoreTranslations();
const currentCountry = ref<string | undefined>(
  storeConfiguration.config?.caas_restServiceShopCountry,
);
const isUpdating = ref<boolean>(false);

const container = computed(() => {
  return storeConfiguration.configShop?.container;
});

const shopContainer = computed(() => {
  return storeConfiguration.configShop?.shopContainer;
});

const props = defineProps({
  item: {
    type: Object,
    default: () => {},
  },
  addButtonLoadingText: {
    type: String,
    default: '',
  },
  addItemFailedMsg: {
    type: String,
    default: '',
  },
  addItemAmountMsgTxt: {
    type: String,
    default: '',
  },
  addItemHighestAmountMsg: {
    type: String,
    default: '',
  },
  addItemLowestAmountMsg: {
    type: String,
    default: '',
  },
  addItemMultipleAmountMsg: {
    type: String,
    default: '',
  },
  isDarkBackground: {
    type: Boolean,
    default: false,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  disableAmountSnackbar: {
    type: Boolean,
    default: false,
  },
  source: {
    type: String,
    default: 'unknown',
  },
  clearNotifications: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['closed', 'error', 'amount-warning', 'is-updating']);

const addItemFailed = ref(false);
const addItemAmountMessage = ref(false);
const waitingForResponse = ref(false);
const addItemAmountMsgTxtRef = ref<string | null>(null);
const translations = ref(storeTranslations);
const errorMessage = ref<string>(
  translations.value['addItem.genericError'] ||
    'Technical error – the item could not be added. Please try again later.',
);
const openGeneralNotification = ref<boolean>(false);
const generalServiceWarning = ref<string>(
  translations?.value['general.service.warning'] || 'Service currently unavailable',
);

const addItem = async () => {
  openGeneralNotification.value = false;
  isUpdating.value = true;

  if (waitingForResponse.value) {
    return;
  }

  addItemFailed.value = false;
  addItemAmountMessage.value = false;
  addItemAmountMsgTxtRef.value = props.addItemAmountMsgTxt;
  let addItemHighestAmountMsg = props.addItemHighestAmountMsg;
  let addItemLowestAmountMsg = props.addItemLowestAmountMsg;
  let addItemMultipleAmountMsg = props.addItemMultipleAmountMsg;
  waitingForResponse.value = true;
  openGeneralNotification.value = false;

  if (!storeConfiguration.config) {
    waitingForResponse.value = false;

    emit('error');
    return;
  }

  let productType: string | undefined;

  try {
    const response = await post<{ ProductType?: string }>(
      withBaseUrl({
        config: storeConfiguration.config,
        url: storeConfiguration.config?.caas_getItemInfoUrl,
      }),
      {
        itemid: props.item.id,
        country: currentCountry.value,
        container: container.value,
        shopContainer: shopContainer.value,
        language: (_window.currentLanguage || storeConfiguration.caasConfig?.language)?.replace(
          'EN-US',
          'EN',
        ),
      },
    )
      .then((data) => {
        if (!data) {
          generalServiceWarning.value =
            translations?.value['general.service.warning'] || 'Service currently not available';
          openGeneralNotification.value = true;
          return;
        }

        return data;
      })
      .catch((error) => {
        generalServiceWarning.value =
          translations?.value['general.service.warning'] ||
          error ||
          'Service currently not available';
        openGeneralNotification.value = true;
      });
    productType = response?.ProductType;
  } catch (error) {
    openGeneralNotification.value = true;

    // This terminates the current function
    return;
  } finally {
    isUpdating.value = false;
  }

  getQuoteIdFromUrl(storeConfiguration.config.caas_getQuoteIdUrl)
    .then((quoteId) => {
      if (!storeConfiguration.config) {
        return;
      }

      post<{ amountMessage?: string; success?: boolean; items?: object }>(
        withBaseUrl({
          config: storeConfiguration.config,
          url: storeConfiguration.config.caas_addItemUrl,
        }),
        {
          itemid: props.item.id,
          quantity: props.item.quantity,
          country: currentCountry.value,
          language: (_window.currentLanguage || storeConfiguration.caasConfig?.language)?.replace(
            'EN-US',
            'EN',
          ),
          quoteid: quoteId,
          headline: props.item.headline,
          subheadline: props.item.subheadline,
          pictureurl: props.item.pictureurl,
          thumbnailurl: props.item.thumbnailurl,
          producturl: props.item.producturl,
          // ? individualPrice extra
          isLoggedIn: Boolean(_window.logIn),
          discount: _window.logIn?.discount,
          typesExcludedDiscount: _window.logIn?.typesExcludedDiscount,
          productType,
          container: container.value,
          shopContainer: shopContainer.value,
        },
      )
        .then((response) => {
          if (!response) {
            waitingForResponse.value = false;
            emit('error');
            return;
          }

          if ((response as ResponseError).error) {
            addItemFailed.value = true;
            waitingForResponse.value = false;

            if ((response as ResponseError).error.indexOf('not found') > -1) {
              errorMessage.value = props.addItemFailedMsg;
            }
          } else {
            waitingForResponse.value = false;

            if (
              typeof response.amountMessage !== 'undefined' &&
              response.amountMessage != null &&
              response.amountMessage != ''
            ) {
              // ? Try to get message and value which is separated by semicolon in json response

              let strAmountMessage = '';
              let partsOfAmountMessage = response.amountMessage.split(';');
              const amountValue = partsOfAmountMessage[1];
              const replaceAmountValue = (message: string) =>
                message.replace(/###amountValue###|#<amountValue>#/g, amountValue);
              if (partsOfAmountMessage[0].includes('highestAmount')) {
                strAmountMessage = replaceAmountValue(addItemHighestAmountMsg);
              }
              if (partsOfAmountMessage[0].includes('lowestAmount')) {
                strAmountMessage = replaceAmountValue(addItemLowestAmountMsg);
              }
              if (partsOfAmountMessage[0].includes('multipleAmount')) {
                strAmountMessage = replaceAmountValue(addItemMultipleAmountMsg);
              }
              addItemAmountMsgTxtRef.value = strAmountMessage;
              if (!props.disableAmountSnackbar) {
                addItemAmountMessage.value = true;
              }
              emit('amount-warning', strAmountMessage);
            }

            const eventData = {
              ...response,
              source: props.source,
            };
            // Also add source to each item
            if (eventData.items && Array.isArray(eventData.items)) {
              eventData.items = eventData.items.map((item) => ({
                ...item,
                source: props.source,
              }));
            }

            const event = new CustomEvent(CustomEvents.ADD_TO_CART, {
              detail: eventData,
            });
            window.dispatchEvent(event);

            const updateCartEvent = new CustomEvent(CustomEvents.UPDATE_SHOP_CART, {});

            window.dispatchEvent(updateCartEvent);
          }

          emit('closed');
        })
        .catch((error) => {
          addItemFailed.value = true;
          waitingForResponse.value = false;
          console.error(error);
          emit('error', error);

          if (error.data) {
            emit('error', error.data);
          }
        });
    })
    .catch((error) => {
      addItemFailed.value = true;
      waitingForResponse.value = false;
      console.error(error);
    })
    .finally(() => {
      isUpdating.value = false;
    });
};

defineExpose({
  addItem,
});

watch(
  () => storeConfiguration,
  (newValue) => {
    if (newValue && newValue.config) {
      currentCountry.value = newValue.config.caas_restServiceShopCountry;
    }
  },
  { immediate: true, deep: true },
);

watch(
  isUpdating,
  (newValue) => {
    emit('is-updating', newValue);
  },
  { immediate: true },
);

watch(
  () => props.clearNotifications,
  (newValue) => {
    if (newValue) {
      openGeneralNotification.value = false;
      addItemFailed.value = false;
      addItemAmountMessage.value = false;
    }
  },
  { immediate: true },
);
</script>

<template>
  <div :class="$style.container">
    <Button1XP
      :class="$style.btn"
      :is-disabled="isDisabled || waitingForResponse"
      start-icon="shopping_cart"
      :text="waitingForResponse ? addButtonLoadingText : item.text"
      variant="secondary"
      @click="addItem"
    />

    <Notifications
      :message="addItemAmountMsgTxtRef ?? ''"
      :open="addItemAmountMessage"
      pattern="inline"
      type="warning"
      @update:open="addItemAmountMessage = $event"
    />

    <Notifications
      :message="errorMessage"
      :open="addItemFailed"
      pattern="persistent"
      type="error"
      @update:open="addItemFailed = $event"
    />
  </div>
</template>

<style module lang="scss" src="./ShopCartLink.scss" />
